import { useState } from 'react'
import type { FormEvent, ReactNode } from 'react'
import { Badge } from '@/components/Badge'
import { Button } from '@/components/Button'
import { Card, CardBody, CardHeader } from '@/components/Card'
import { CopyButton } from '@/components/CopyButton'
import { ConfirmDialog } from '@/components/Dialog'
import { EmptyState } from '@/components/EmptyState'
import { ErrorNotice } from '@/components/ErrorNotice'
import { SelectField, TextField } from '@/components/Form'
import { SkeletonRows } from '@/components/Skeleton'
import {
  useAdminInvites,
  useAdminProfiles,
  useCreateInvite,
  useRevokeInvite,
} from '@/hooks/useAdmin'
import { useNow } from '@/hooks/useNow'
import { formatDate, inviteUrl, relativeTime } from '@/lib/format'
import type { Invite, InviteInput } from '@/lib/types'

const EXPIRY_OPTIONS = [
  { value: '', label: 'Never expires' },
  { value: '1', label: '1 day' },
  { value: '7', label: '7 days' },
  { value: '30', label: '30 days' },
]

function daysFromNow(days: string): string | null {
  if (!days) return null
  const date = new Date(Date.now() + Number(days) * 24 * 60 * 60 * 1000)
  return date.toISOString()
}

function inviteState(
  invite: Invite,
  now: number,
): { label: string; tone: 'ok' | 'neutral' | 'warn' } {
  if (invite.revokedAt) return { label: 'Revoked', tone: 'neutral' }
  if (invite.expiresAt && new Date(invite.expiresAt).getTime() < now) {
    return { label: 'Expired', tone: 'neutral' }
  }
  if (invite.maxUses !== null && invite.usedCount >= invite.maxUses) {
    return { label: 'Used up', tone: 'warn' }
  }
  return { label: 'Active', tone: 'ok' }
}

const usesLabel = (invite: Invite): string =>
  `${invite.usedCount}${invite.maxUses !== null ? `/${invite.maxUses}` : '/∞'}`

function Field({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="signage text-[9px] text-faint">{label}</dt>
      <dd className="mt-0.5 truncate font-mono text-xs text-ink">{value}</dd>
    </div>
  )
}

const TH = 'signage px-4 py-2.5 text-[9px] font-semibold text-faint'
const TD = 'px-4 py-3 align-middle'

export function AdminInvitesPage() {
  const invites = useAdminInvites()
  const profiles = useAdminProfiles()
  const create = useCreateInvite()
  const revoke = useRevokeInvite()
  const now = useNow(60_000)

  const [label, setLabel] = useState('')
  const [profileId, setProfileId] = useState('')
  const [expiryDays, setExpiryDays] = useState('7')
  const [maxUses, setMaxUses] = useState('1')
  const [accountExpiryDays, setAccountExpiryDays] = useState('')
  const [pendingRevoke, setPendingRevoke] = useState<Invite | null>(null)

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const input: InviteInput = {
      label: label.trim() || null,
      profileId: profileId || null,
      expiresAt: daysFromNow(expiryDays),
      maxUses: maxUses.trim() ? Number(maxUses) : null,
      accountExpiryDays: accountExpiryDays.trim() ? Number(accountExpiryDays) : null,
    }
    create.mutate(input, { onSuccess: () => setLabel('') })
  }

  const items = invites.data?.items ?? []

  return (
    <div className="space-y-5">
      <Card>
        <CardHeader
          title="Create an invite"
          description="Share the link; the code works the moment it exists."
        />
        <CardBody>
          <form onSubmit={onSubmit} className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
            <TextField
              label="Label"
              placeholder="e.g. Sam from work"
              value={label}
              onChange={(event) => setLabel(event.target.value)}
            />
            <SelectField
              label="Profile"
              value={profileId}
              onChange={(event) => setProfileId(event.target.value)}
            >
              <option value="">Server default</option>
              {(profiles.data?.items ?? []).map((profile) => (
                <option key={profile.id} value={profile.id}>
                  {profile.name}
                </option>
              ))}
            </SelectField>
            <SelectField
              label="Link expires"
              value={expiryDays}
              onChange={(event) => setExpiryDays(event.target.value)}
            >
              {EXPIRY_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </SelectField>
            <TextField
              label="Max uses"
              type="number"
              min={1}
              placeholder="Unlimited"
              value={maxUses}
              onChange={(event) => setMaxUses(event.target.value)}
            />
            <TextField
              label="Account expiry (days)"
              type="number"
              min={1}
              placeholder="From profile"
              value={accountExpiryDays}
              onChange={(event) => setAccountExpiryDays(event.target.value)}
            />
            <div className="sm:col-span-2 lg:col-span-5">
              <Button
                type="submit"
                variant="primary"
                className="w-full sm:w-auto"
                loading={create.isPending}
              >
                Create invite
              </Button>
            </div>
          </form>
        </CardBody>
      </Card>

      <Card>
        <CardHeader title={`Invites${items.length ? ` (${items.length})` : ''}`} />
        <CardBody flush>
          {invites.isPending ? (
            <div className="p-4">
              <SkeletonRows rows={4} />
            </div>
          ) : invites.isError ? (
            <div className="p-4">
              <ErrorNotice error={invites.error} onRetry={() => void invites.refetch()} />
            </div>
          ) : items.length === 0 ? (
            <div className="p-4">
              <EmptyState
                title="No invites yet"
                description="Create one above and share the link — whoever gets it picks their own username and password."
              />
            </div>
          ) : (
            <>
              {/* Phones: one stacked record per invite, code first. */}
              <div className="sm:hidden">
                {items.map((invite) => {
                  const state = inviteState(invite, now)
                  return (
                    <div key={invite.id} className="board-row px-4 py-3.5">
                      <div className="flex items-start justify-between gap-2">
                        <p className="min-w-0 truncate font-mono text-sm text-ink">{invite.code}</p>
                        <div className="flex shrink-0 items-center gap-1.5">
                          {invite.kind === 'referral' ? <Badge>Referral</Badge> : null}
                          <Badge tone={state.tone}>{state.label}</Badge>
                        </div>
                      </div>

                      <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2.5">
                        <Field label="Label" value={invite.label ?? '—'} />
                        <Field label="Profile" value={invite.profileName ?? 'Server default'} />
                        <Field label="Used" value={usesLabel(invite)} />
                        <Field
                          label="Expires"
                          value={invite.expiresAt ? formatDate(invite.expiresAt) : 'Never'}
                        />
                      </dl>

                      <div className="mt-3 grid grid-cols-2 gap-2">
                        <CopyButton value={inviteUrl(invite.code)} label="Copy link" />
                        {!invite.revokedAt ? (
                          <Button size="sm" variant="ghost" onClick={() => setPendingRevoke(invite)}>
                            Revoke
                          </Button>
                        ) : null}
                      </div>
                    </div>
                  )
                })}
              </div>

              <div className="hidden sm:block">
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b border-line">
                      <th scope="col" className={TH}>
                        Code
                      </th>
                      <th scope="col" className={TH}>
                        Label
                      </th>
                      <th scope="col" className={TH}>
                        Used
                      </th>
                      <th scope="col" className={TH}>
                        Expires
                      </th>
                      <th scope="col" className={`${TH} text-right`}>
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((invite) => {
                      const state = inviteState(invite, now)
                      return (
                        <tr key={invite.id} className="border-b border-line last:border-b-0">
                          <td className={TD}>
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="font-mono text-sm text-ink">{invite.code}</span>
                              <Badge tone={state.tone}>{state.label}</Badge>
                              {invite.kind === 'referral' ? <Badge>Referral</Badge> : null}
                            </div>
                            <p className="mt-0.5 font-mono text-[11px] text-faint">
                              created {relativeTime(invite.createdAt, now)}
                            </p>
                          </td>
                          <td className={`${TD} text-xs text-muted`}>
                            {invite.label ?? '—'}
                            {invite.profileName ? (
                              <span className="block font-mono text-[11px] text-faint">
                                {invite.profileName}
                              </span>
                            ) : null}
                          </td>
                          <td className={`${TD} font-mono text-xs text-muted`}>
                            {usesLabel(invite)}
                          </td>
                          <td className={`${TD} font-mono text-xs text-muted`}>
                            {invite.expiresAt ? formatDate(invite.expiresAt) : 'Never'}
                          </td>
                          <td className={TD}>
                            <div className="flex justify-end gap-2">
                              <CopyButton value={inviteUrl(invite.code)} label="Copy link" />
                              {!invite.revokedAt ? (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => setPendingRevoke(invite)}
                                >
                                  Revoke
                                </Button>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </CardBody>
      </Card>

      <ConfirmDialog
        open={pendingRevoke !== null}
        onClose={() => setPendingRevoke(null)}
        loading={revoke.isPending}
        destructive
        confirmLabel="Revoke invite"
        title="Revoke this invite?"
        description="The link stops working immediately. Accounts already created with it are unaffected."
        onConfirm={() => {
          if (!pendingRevoke) return
          revoke.mutate(pendingRevoke.id, { onSettled: () => setPendingRevoke(null) })
        }}
      />
    </div>
  )
}
